import { Navigate, Route, Routes, useParams } from "react-router";
import { MeProvider } from "~/auth/MeProvider";
import { NotFoundScreen } from "~/screens/NotFoundScreen";
import {
  BookingCancelScreen,
  BookingRescheduleScreen,
  RescheduleSuggestionScreen,
} from "~/screens/consumer/BookingActionScreens";
import { ConsumerBookingsScreen } from "~/screens/consumer/ConsumerBookingsScreen";
import { BusinessBookingsScreen } from "~/screens/business/BusinessBookingsScreen";
import {
  AdminPlatformScreen,
  AdminRegistrationQueueScreen,
} from "~/screens/admin/AdminScreens";
import { BusinessCalendarScreen } from "~/screens/business/BusinessCalendarScreen";
import { BusinessHomeScreen } from "~/screens/business/BusinessHomeScreen";
import { BusinessInsightsScreen } from "~/screens/business/BusinessInsightsScreen";
import {
  PractitionerCalendarScreen,
  ScheduleScreen,
} from "~/screens/business/ScheduleScreens";
import { BusinessSettingsScreen } from "~/screens/business/BusinessSettingsScreen";
import {
  BusinessDetailsScreen,
  JoinAccountScreen,
} from "~/screens/business/JoinScreens";
import {
  CustomerFileScreen,
  CustomersScreen,
} from "~/screens/business/CustomerScreens";
import {
  BusinessProfileEditScreen,
  ClinicOpeningHoursScreen,
  PractitionersScreen,
  ServicesScreen,
} from "~/screens/business/SettingsToolScreens";
import { ConsumerProfileScreen } from "~/screens/consumer/ConsumerProfileScreen";
import { FavoritesScreen } from "~/screens/consumer/FavoritesScreen";
import { InboxScreen } from "~/screens/consumer/InboxScreen";
import {
  ConsumerMealPlanScreen,
} from "~/screens/consumer/MealPlanScreens";
import { ConsumerCareScreen } from "~/screens/consumer/CareScreen";
import {
  MealPlanEditorScreen,
  MealPlanTemplateEditorScreen,
  MealPlanTemplatesScreen,
  NewMealPlanScreen,
} from "~/screens/business/MealPlanScreens";
import { BusinessPublicProfileScreen } from "~/screens/public/BusinessPublicProfileScreen";
import { ClinicBookingScreen } from "~/screens/public/ClinicBookingScreen";
import { LandingScreen } from "~/screens/public/LandingScreen";
import { SearchScreen } from "~/screens/public/SearchScreen";
import {
  PrivacyScreen,
  SupportScreen,
} from "~/screens/public/StaticScreens";
import { AdminLogInScreen } from "~/screens/auth/AdminLogInScreen";
import { LogInScreen } from "~/screens/auth/LogInScreen";
import { PhoneVerificationScreen } from "~/screens/auth/PhoneVerificationScreen";
import { SignUpScreen } from "~/screens/auth/SignUpScreen";
import { StaffLogInScreen } from "~/screens/auth/StaffLogInScreen";
import { StaffPasswordChangeScreen } from "~/screens/auth/StaffPasswordChangeScreen";
import {
  AccountOnly,
  AdminOnly,
  BusinessOnly,
  ManagerOnly,
  ConsumerOnly,
  ConsumerSurface,
  GuestOnly,
  PasswordChangeOnly,
} from "~/routes/guards";

/**
 * Every route the Next host serves, on the static client (R22 T10).
 *
 * **Every screen is ported** (T11 to T15, finished 24 August 2026). Standing the
 * whole route table up at the scaffold and marking each unported screen with a
 * `PortPending` placeholder is what made the port checkable: a screen that was
 * quietly forgotten would have been visible rather than an absence nobody
 * noticed until a user found a 404 after cutover. That placeholder is now
 * deleted, because nothing is owed.
 *
 * **The paths match the Next host's exactly**, which is not a coincidence and
 * not optional: the same URLs are in WhatsApp messages already sent, in
 * browser bookmarks, and in the `roleHome` policy both frontends share. A
 * cutover that renamed a route would break links that exist in the world.
 */
/** The H45 redirect, as a component so it can read the id it forwards. */
function AnyPractitionerRedirect() {
  const { id } = useParams();
  return <Navigate to={`/businesses/${id ?? ""}`} replace />;
}

export default function App() {
  return (
    <MeProvider>
      <Routes>
        {/* ── Guest and Consumer surfaces (H28) ──────────────────────────── */}
        <Route
          path="/"
          element={
            <ConsumerSurface>
              <LandingScreen />
            </ConsumerSurface>
          }
        />
        <Route
          path="/search"
          element={
            <ConsumerSurface>
              <SearchScreen />
            </ConsumerSurface>
          }
        />
        <Route
          path="/businesses/:id"
          element={
            <ConsumerSurface>
              <BusinessPublicProfileScreen />
            </ConsumerSurface>
          }
        />
        {/* H59: booking is its own screen, behind the details view's
            "Book an appointment". Same guard: Consumers and guests only. */}
        <Route
          path="/businesses/:id/book"
          element={
            <ConsumerSurface>
              <ClinicBookingScreen />
            </ConsumerSurface>
          }
        />
        {/* H45 left this URL with nothing to be an alternative to: a Consumer
            never picks a practitioner, so every clinic books the one way and
            the profile *is* this page. It stays as a redirect rather than a
            deletion because the link is bookmarkable and was shared. */}
        <Route
          path="/businesses/:id/any"
          element={<AnyPractitionerRedirect />}
        />

        {/* ── The ways in ────────────────────────────────────────────────── */}
        <Route
          path="/login"
          element={
            <GuestOnly>
              <LogInScreen />
            </GuestOnly>
          }
        />
        <Route
          path="/login/staff"
          element={
            <GuestOnly>
              <StaffLogInScreen />
            </GuestOnly>
          }
        />
        <Route
          path="/login/admin"
          element={
            <GuestOnly>
              <AdminLogInScreen />
            </GuestOnly>
          }
        />
        <Route
          path="/signup"
          element={
            <GuestOnly>
              <SignUpScreen />
            </GuestOnly>
          }
        />
        <Route
          path="/verify"
          element={
            <GuestOnly>
              <PhoneVerificationScreen />
            </GuestOnly>
          }
        />
        {/* Not GuestOnly: it is reached *by* a signed-in member, and only by
            one whose password is still the generated one (H44). */}
        <Route
          path="/staff/password"
          element={
            <PasswordChangeOnly>
              <StaffPasswordChangeScreen />
            </PasswordChangeOnly>
          }
        />

        {/* ── The Consumer's own screens ─────────────────────────────────── */}
        <Route
          path="/bookings"
          element={
            <ConsumerOnly>
              <ConsumerBookingsScreen />
            </ConsumerOnly>
          }
        />
        <Route
          path="/bookings/:id/cancel"
          element={
            <ConsumerOnly>
              <BookingCancelScreen />
            </ConsumerOnly>
          }
        />
        <Route
          path="/bookings/:id/reschedule"
          element={
            <ConsumerOnly>
              <BookingRescheduleScreen />
            </ConsumerOnly>
          }
        />
        <Route
          path="/bookings/:id/suggestion"
          element={
            <ConsumerOnly>
              <RescheduleSuggestionScreen />
            </ConsumerOnly>
          }
        />
        <Route
          path="/favorites"
          element={
            <ConsumerOnly>
              <FavoritesScreen />
            </ConsumerOnly>
          }
        />
        {/* H55: My care, what the Consumer's clinics prepared for them. */}
        <Route
          path="/care"
          element={
            <ConsumerOnly>
              <ConsumerCareScreen />
            </ConsumerOnly>
          }
        />
        {/* H53: a plan a nutrition clinic shared. The plan's path is the one
            the WhatsApp message links to, so it may never move. The list
            became a section of My care (H55); its old address goes there,
            which now carries everything it showed. */}
        <Route path="/meal-plans" element={<Navigate to="/care" replace />} />
        <Route
          path="/meal-plans/:planId"
          element={
            <ConsumerOnly>
              <ConsumerMealPlanScreen />
            </ConsumerOnly>
          }
        />
        <Route
          path="/profile"
          element={
            <ConsumerOnly>
              <ConsumerProfileScreen />
            </ConsumerOnly>
          }
        />
        {/* An inbox belongs to an **account**, not to a role: a member and
            an admin have one too, and the Next host guarded it that way. */}
        <Route
          path="/inbox"
          element={
            <AccountOnly>
              <InboxScreen />
            </AccountOnly>
          }
        />

        {/* ── The business dashboard ─────────────────────────────────────── */}
        <Route
          path="/business"
          element={
            <BusinessOnly>
              <BusinessHomeScreen />
            </BusinessOnly>
          }
        />
        <Route
          path="/business/bookings"
          element={
            <BusinessOnly>
              <BusinessBookingsScreen />
            </BusinessOnly>
          }
        />
        <Route
          path="/business/calendar"
          element={
            <BusinessOnly>
              <BusinessCalendarScreen />
            </BusinessOnly>
          }
        />
        <Route
          path="/business/hours"
          element={
            <ManagerOnly>
              <ClinicOpeningHoursScreen />
            </ManagerOnly>
          }
        />
        <Route
          path="/business/schedule"
          element={
            <ManagerOnly>
              <ScheduleScreen />
            </ManagerOnly>
          }
        />
        <Route
          path="/business/schedule/:practitionerId"
          element={
            <ManagerOnly>
              <PractitionerCalendarScreen />
            </ManagerOnly>
          }
        />
        <Route
          path="/business/services"
          element={
            <ManagerOnly>
              <ServicesScreen />
            </ManagerOnly>
          }
        />
        <Route
          path="/business/practitioners"
          element={
            <ManagerOnly>
              <PractitionersScreen />
            </ManagerOnly>
          }
        />
        <Route
          path="/business/customers"
          element={
            <BusinessOnly>
              <CustomersScreen />
            </BusinessOnly>
          }
        />
        <Route
          path="/business/customers/:consumerId"
          element={
            <BusinessOnly>
              <CustomerFileScreen />
            </BusinessOnly>
          }
        />
        {/* H53: both tiers, like the Customer File they hang off. */}
        <Route
          path="/business/customers/:consumerId/meal-plans/new"
          element={
            <BusinessOnly>
              <NewMealPlanScreen />
            </BusinessOnly>
          }
        />
        <Route
          path="/business/meal-plans/:planId"
          element={
            <BusinessOnly>
              <MealPlanEditorScreen />
            </BusinessOnly>
          }
        />
        <Route
          path="/business/meal-plan-templates"
          element={
            <BusinessOnly>
              <MealPlanTemplatesScreen />
            </BusinessOnly>
          }
        />
        <Route
          path="/business/meal-plan-templates/:templateId"
          element={
            <BusinessOnly>
              <MealPlanTemplateEditorScreen />
            </BusinessOnly>
          }
        />
        <Route
          path="/business/insights"
          element={
            <ManagerOnly>
              <BusinessInsightsScreen />
            </ManagerOnly>
          }
        />
        <Route
          path="/business/profile"
          element={
            <ManagerOnly>
              <BusinessProfileEditScreen />
            </ManagerOnly>
          }
        />
        <Route
          path="/business/settings"
          element={
            <BusinessOnly>
              <BusinessSettingsScreen />
            </BusinessOnly>
          }
        />

        {/* Onboarding sits outside the dashboard chrome and its H44 gate: an
            unaffiliated member is exactly who these screens are for. */}
        <Route path="/join" element={<JoinAccountScreen />} />
        <Route
          path="/join/business"
          element={<BusinessDetailsScreen />}
        />

        {/* ── The admin console ──────────────────────────────────────────── */}
        <Route
          path="/admin"
          element={
            <AdminOnly>
              <AdminRegistrationQueueScreen />
            </AdminOnly>
          }
        />
        <Route
          path="/admin/insights"
          element={
            <AdminOnly>
              <AdminPlatformScreen />
            </AdminOnly>
          }
        />

        {/* ── Static pages, open to everybody ────────────────────────────── */}
        <Route path="/privacy" element={<PrivacyScreen />} />
        <Route path="/support" element={<SupportScreen />} />

        <Route path="*" element={<NotFoundScreen />} />
      </Routes>
    </MeProvider>
  );
}
